/* eslint-disable react/jsx-one-expression-per-line */
import { ReactElement, useContext, useEffect, useState } from 'react';
import { Field, useFormikContext } from 'formik';
import { Alert, Button, DateInput, Modal, TextInput } from '@components';
import { navigateToError } from '@utils';
import VicFinesContext from '@pages/forms/VicFinesFormContext';
import InfoIcon from '../../../../images/info.svg';
import QuestionIcon from '../../../../images/question.svg';

import styles from './FVSStep2.module.scss';

const { REACT_APP_SITECORE_API_ENDPOINT: API_URL } = process.env;

function FVSStep2(): ReactElement {
  const [loading, setLoading] = useState(true);
  const context = useFormikContext();
  const { apiError } = useContext(VicFinesContext);
  const [hasOffenceDate, setOffenceDate] = useState(false);
  const [hasHearingDate, setHearingDate] = useState(false);
  const [hasLicenceNumber, setLicenceNumber] = useState(false);
  const [hasVehicleRego, setVehicleRego] = useState(false);
  const [hasDOB, setDOB] = useState(false);
  const [isOpenHelp, setOpenHelp] = useState(false);
  const FVSStep2AlertContent = (): ReactElement => <p>{`${apiError?.Error}`}</p>;

  const closeNotifications = () => {
    setOpenHelp(false);
  };

  const notificationInformation = (): ReactElement => (
    <div className={styles.notificationDiv}>
      <div>
        <span>If you don't know these details or think we may have the wrong information, contact Fines Victoria, Monday to Friday, 8am to 6pm (except public holidays).</span>
        <p>
          For infringement enquiries, call{' '}
          <a className={styles.contactLink} href="tel:0392008111" title="Fines Victoria phone number">
            03 9200 8111
          </a>{' '}
          or{' '}
          <a className={styles.contactLink} href="tel:1300369819" title="Fines Victoria phone number">
            1300 369 819
          </a>{' '}
          for regional callers.
        </p>
        <p>
          For Notice of Final Demand enquiries, call call{' '}
          <a className={styles.contactLink} href="tel:0392008222" title="Fines Victoria phone number">
            03 9200 8222
          </a>{' '}
          or{' '}
          <a className={styles.contactLink} href="tel:1800150410" title="Fines Victoria phone number">
            1800 150 410
          </a>{' '}
          for regional callers.
        </p>
      </div>
      <p className={styles.sectionTitle}>Do you have a Victorian driver licence?</p>
      <div>
        <span>If asked for your driver licence number, make sure you include any zeros at the start of your Victorian driver licence number.</span>
      </div>
      <div className={styles.btnFooter}>
        <Button onClick={closeNotifications} color="blue" size="small">
          Continue
        </Button>
      </div>
    </div>
  );

  useEffect(() => {
    const setupDebtor = async (formikContext: any): Promise<void> => {
      setLoading(true);
      const payload = {
        obligationNumber: formikContext.values.OBLIGATION_NUMBER,
      };
      const url = `${API_URL}sitecore/FVS/SetupDebtor`;
      let response: any;
      try {
        response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });
        const responseBody = await response.json();
        setLoading(false);
        if (response.status === 200) {
          context.setFieldValue('OffenceDateRequired', false);
          context.setFieldValue('HearingDateRequired', false);
          context.setFieldValue('LicenceRequired', false);
          context.setFieldValue('RegistrationRequired', false);
          context.setFieldValue('DOBRequired', false);
          if (responseBody.OffenceDate) {
            setOffenceDate(true);
            context.setFieldValue('OffenceDateRequired', true);
            context.setFieldTouched('OFFENCE_DATE', false);
          }
          if (responseBody.HearingDate) {
            setHearingDate(true);
            context.setFieldValue('HearingDateRequired', true);
            context.setFieldTouched('HEARING_DATE', false);
          }
          if (responseBody.LicenceRequired) {
            setLicenceNumber(true);
            context.setFieldValue('LicenceRequired', true);
            context.setFieldTouched('DRIVER_LICENCE', false);
          }
          if (responseBody.RegistrationRequired) {
            setVehicleRego(true);
            context.setFieldValue('RegistrationRequired', true);
            context.setFieldTouched('VEHICLE_REGISTRATION', false);
          }
          if (responseBody.DateOfBirthRequired) {
            setDOB(true);
            context.setFieldValue('DOBRequired', true);
            context.setFieldTouched('DOB', false);
          }
        } else navigateToError();
      } catch (error) {
        navigateToError();
      }
    };
    setupDebtor(context);
    return () => {
      setOffenceDate(false); // clean up state to avoid warnings
      setHearingDate(false);
      setLicenceNumber(false);
      setVehicleRego(false);
      setDOB(false);
    };
  }, []);

  return (
    <>
      {!loading && (
        <div>
          <span className="vfH2">More information needed</span>
          <span className="subHeadingH3">We need you to enter some more details about this fine before you can continue.</span>
          {apiError?.Error && <Alert icon={InfoIcon} color="red" message={<FVSStep2AlertContent />} theme="error" />}
          {hasOffenceDate && (
            <Field component={DateInput} name="OFFENCE_DATE" title="Offence date" isMandatory maxDate={new Date()} minDate={new Date(1900, 1, 1)} autocomplete="off" />
          )}
          {hasHearingDate && <Field component={DateInput} name="HEARING_DATE" title="Hearing date" isMandatory minDate={new Date(1900, 1, 1)} autocomplete="off" />}
          {hasLicenceNumber && <Field name="DRIVER_LICENCE" component={TextInput} title="Driver licence number" isMandatory autocomplete="off" />}
          {hasVehicleRego && <Field name="VEHICLE_REGISTRATION" component={TextInput} title="Vehicle registration number" placeholder="ABC123" isMandatory autocomplete="off" />}
          {hasDOB && <Field component={DateInput} name="DOB" title="Date of birth" isMandatory minDate={new Date(1900, 1, 1)} maxDate={new Date()} autocomplete="off" />}
          <div>
            <button type="button" onClick={() => setOpenHelp(true)} className={styles.iconButton}>
              <img src={QuestionIcon} alt="help icon" />
              <span>I don't know these details</span>
            </button>
          </div>
          {isOpenHelp && <Modal close={closeNotifications} show title="Don't know these details?" content={notificationInformation()} />}
        </div>
      )}
    </>
  );
}

export default FVSStep2;
